import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ProductCard from "./ProductCard";
import { MyDomain } from "../App";
import { FiFilter, FiChevronDown, FiChevronUp, FiX, FiGrid, FiSliders } from "react-icons/fi";

function normalizeProduct(p) {
  const hasDiscount = !!(p.priceAfterDiscount && p.price && p.priceAfterDiscount < p.price);
  const discountPercent = p.discount ||
    (hasDiscount ? Math.round((1 - p.priceAfterDiscount / p.price) * 100) : null);
  const dateStr = p.datePost;
  let isNew = false;
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d)) isNew = (Date.now() - d.getTime()) / 86400000 <= 30;
  }
  return {
    id: p.id,
    name: p.name || "",
    image: p.image ? (p.image.startsWith("http") ? p.image : `${MyDomain}${p.image}`) : "",
    price: hasDiscount ? p.priceAfterDiscount : (p.price || 0),
    originalPrice: hasDiscount ? p.price : null,
    hasDiscount, discountPercent, isNew,
    category: p.category || "",
    brand: p.brand || "",
    gender: p.gender || "",
    deal: p.deal || "",
    rating: 0,
  };
}

const SORT_OPTIONS = [
  { value: "default",    label: "Default" },
  { value: "price-asc",  label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "newest",     label: "Newest First" },
  { value: "rating",     label: "Top Rated" },
];

function FilterSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: "1px solid rgba(0,0,0,0.07)", paddingBottom: 16, marginBottom: 16 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: "0 0 10px", color: "#1a1a1a", fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}
      >
        {title}
        {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  const [hovered, setHovered] = useState(false);
  return (
    <label
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", padding: "5px 0", color: checked ? "#e11d48" : hovered ? "#333" : "#555", fontSize: 13, transition: "color 0.15s" }}
    >
      <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${checked ? "#e11d48" : "rgba(0,0,0,0.2)"}`, background: checked ? "#e11d48" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
        {checked && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: "none" }} />
      {label}
    </label>
  );
}

function StarRating({ value }) {
  return (
    <span style={{ display: "inline-flex", gap: 1 }}>
      {[1,2,3,4,5].map((s) => (
        <svg key={s} width="13" height="13" viewBox="0 0 24 24" fill={s <= value ? "#f59e0b" : "none"} stroke={s <= value ? "#f59e0b" : "#ccc"} strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

const ITEMS_PER_PAGE = 12;

export default function Category() {
  const { categoryName } = useParams();
  const displayName = categoryName
    ? categoryName.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "All Products";

  const [allProducts, setAllProducts]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [fetchError, setFetchError]     = useState(null);

  useEffect(() => {
    if (!categoryName) return;
    setLoading(true); setFetchError(null);
    axios.post(`${MyDomain}Products2/GetProducts`, { Category: categoryName })
      .then(({ data }) => { setAllProducts((Array.isArray(data) ? data : []).map(normalizeProduct)); setLoading(false); })
      .catch((err) => { setFetchError(err.message); setLoading(false); });
  }, [categoryName]);

  const categoryOptions = useMemo(() => [...new Set(allProducts.map((p) => p.category).filter(Boolean))], [allProducts]);
  const brandOptions    = useMemo(() => [...new Set(allProducts.map((p) => p.brand).filter(Boolean))], [allProducts]);
  const genderOptions   = useMemo(() => [...new Set(allProducts.map((p) => p.gender).filter(Boolean))], [allProducts]);

  const [selectedCategories, setSelectedCategories] = useState([]);
  useEffect(() => {
    if (categoryName) {
      const slug = categoryName.replace(/-/g, " ");
      const match = categoryOptions.find((c) => c.toLowerCase() === slug.toLowerCase());
      setSelectedCategories(match ? [match] : []);
    } else {
      setSelectedCategories([]);
    }
  }, [categoryName, categoryOptions]);

  const [selectedBrands, setSelectedBrands]   = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [minPrice, setMinPrice]               = useState("");
  const [maxPrice, setMaxPrice]               = useState("");
  const [onlyNew, setOnlyNew]                 = useState(false);
  const [onlyDiscount, setOnlyDiscount]       = useState(false);
  const [minRating, setMinRating]             = useState(null);
  const [sortBy, setSortBy]                   = useState("default");
  const [page, setPage]                       = useState(1);
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [sortOpen, setSortOpen]               = useState(false);

  const toggleItem = (list, setList, value) =>
    setList((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);

  const activeFilterCount =
    selectedCategories.length + selectedBrands.length + selectedGenders.length +
    (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) + (onlyNew ? 1 : 0) +
    (onlyDiscount ? 1 : 0) + (minRating ? 1 : 0);

  const clearAll = () => {
    setSelectedCategories([]); setSelectedBrands([]); setSelectedGenders([]);
    setMinPrice(""); setMaxPrice(""); setOnlyNew(false);
    setOnlyDiscount(false); setMinRating(null); setPage(1);
  };

  const filtered = useMemo(() => {
    let list = allProducts
      .filter((p) => !selectedCategories.length || selectedCategories.includes(p.category))
      .filter((p) => !selectedBrands.length  || selectedBrands.includes(p.brand))
      .filter((p) => !selectedGenders.length || selectedGenders.includes(p.gender))
      .filter((p) => !minPrice    || p.price >= parseFloat(minPrice))
      .filter((p) => !maxPrice    || p.price <= parseFloat(maxPrice))
      .filter((p) => !onlyNew     || p.isNew)
      .filter((p) => !onlyDiscount|| p.hasDiscount)
      .filter((p) => !minRating   || p.rating >= minRating);
    return [...list].sort((a, b) => {
      if (sortBy === "price-asc")  return a.price - b.price;
      if (sortBy === "price-desc") return b.price - a.price;
      if (sortBy === "newest")     return b.isNew - a.isNew;
      if (sortBy === "rating")     return b.rating - a.rating;
      return 0;
    });
  }, [selectedCategories, selectedBrands, selectedGenders, minPrice, maxPrice, onlyNew, onlyDiscount, minRating, sortBy, allProducts]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const currentSort = SORT_OPTIONS.find((o) => o.value === sortBy);

  const SidebarContent = (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.08)", padding: "20px 18px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 7 }}>
          <FiSliders size={15} /> Filters
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {activeFilterCount > 0 && (
            <button onClick={clearAll} style={{ background: "none", border: "none", color: "#e11d48", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              <FiX size={13} /> Clear all
            </button>
          )}
          <button onClick={() => setSidebarOpen(false)} className="sidebar-close-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", display: "flex" }}>
            <FiX size={18} />
          </button>
        </div>
      </div>

      <FilterSection title="Category">
        {categoryOptions.map((cat) => (
          <CheckRow key={cat} label={cat} checked={selectedCategories.includes(cat)}
            onChange={() => { toggleItem(selectedCategories, setSelectedCategories, cat); setPage(1); }} />
        ))}
      </FilterSection>

      <FilterSection title="Brand">
        {brandOptions.map((brand) => (
          <CheckRow key={brand} label={brand} checked={selectedBrands.includes(brand)}
            onChange={() => { toggleItem(selectedBrands, setSelectedBrands, brand); setPage(1); }} />
        ))}
      </FilterSection>

      {genderOptions.length > 0 && (
        <FilterSection title="Gender">
          {genderOptions.map((gender) => (
            <CheckRow key={gender} label={gender} checked={selectedGenders.includes(gender)}
              onChange={() => { toggleItem(selectedGenders, setSelectedGenders, gender); setPage(1); }} />
          ))}
        </FilterSection>
      )}

      <FilterSection title="Price Range">
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="number" placeholder="Min" value={minPrice}
            onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
            style={{ flex: 1, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#1a1a1a", outline: "none", width: 0 }} />
          <span style={{ color: "#aaa", fontSize: 12 }}>–</span>
          <input type="number" placeholder="Max" value={maxPrice}
            onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
            style={{ flex: 1, border: "1px solid rgba(0,0,0,0.15)", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#1a1a1a", outline: "none", width: 0 }} />
        </div>
      </FilterSection>

      <FilterSection title="Minimum Rating">
        {[4, 3, 2].map((r) => (
          <label key={r} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "5px 0" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0, border: `1.5px solid ${minRating === r ? "#e11d48" : "rgba(0,0,0,0.2)"}`, background: minRating === r ? "#e11d48" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
              {minRating === r && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
            </div>
            <input type="radio" checked={minRating === r} onChange={() => { setMinRating(r); setPage(1); }} style={{ display: "none" }} />
            <StarRating value={r} />
            <span style={{ fontSize: 12, color: "#777" }}>& up</span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="Quick Filters">
        <CheckRow label="New Arrivals" checked={onlyNew} onChange={() => { setOnlyNew((v) => !v); setPage(1); }} />
        <CheckRow label="On Sale" checked={onlyDiscount} onChange={() => { setOnlyDiscount((v) => !v); setPage(1); }} />
      </FilterSection>
    </div>
  );

  if (loading)
    return <div style={{ padding: "80px 16px", textAlign: "center", color: "#999", fontSize: 15 }}>Loading products…</div>;

  if (fetchError)
    return <div style={{ padding: "80px 16px", textAlign: "center", color: "#e11d48", fontSize: 15 }}>Failed to load products: {fetchError}</div>;

  return (
    <>
      <div className="cat-page" style={{ overflowX: "hidden" }}>
        {/* Page title */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ color: "#1a1a1a", fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>{displayName}</h1>
          {categoryName === "dubai-perfumes" && (
            <p style={{ fontSize: 24, fontWeight: 800, color: "#1a1a1a", margin: "12px 0 8px", letterSpacing: 0.5 }}>
              Original Dubai Perfumes
            </p>
          )}
          <p style={{ color: "#999", fontSize: 13, margin: 0 }}>
            Home &rsaquo; <span style={{ color: "#1a1a1a" }}>{displayName}</span>
          </p>
        </div>

        {/* Toolbar */}
        <div className="cat-toolbar">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => setSidebarOpen((o) => !o)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: sidebarOpen ? "#e11d48" : "rgba(0,0,0,0.05)", color: sidebarOpen ? "#fff" : "#333", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", whiteSpace: "nowrap" }}
            >
              <FiFilter size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span style={{ background: sidebarOpen ? "rgba(255,255,255,0.3)" : "#e11d48", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            <span style={{ color: "#999", fontSize: 13 }}>
              <strong style={{ color: "#1a1a1a" }}>{filtered.length}</strong> products
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="sort-label">Sort by:</span>
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setSortOpen((o) => !o)}
                className="sort-btn"
              >
                <FiGrid size={13} />
                {currentSort.label}
                <FiChevronDown size={13} style={{ marginLeft: "auto" }} />
              </button>
              {sortOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, overflow: "hidden", minWidth: 190, zIndex: 200, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
                  {SORT_OPTIONS.map((opt) => (
                    <button key={opt.value}
                      onClick={() => { setSortBy(opt.value); setSortOpen(false); setPage(1); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", border: "none", fontSize: 13, background: sortBy === opt.value ? "rgba(225,29,72,0.06)" : "none", color: sortBy === opt.value ? "#e11d48" : "#333", fontWeight: sortBy === opt.value ? 600 : 400, cursor: "pointer" }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* Sidebar — desktop inline / mobile bottom drawer */}
          {sidebarOpen && (
            <div className="cat-sidebar-wrap">{SidebarContent}</div>
          )}

          {/* Grid */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {paginated.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 0", color: "#aaa", fontSize: 15 }}>
                No products match your filters.<br />
                <button onClick={clearAll} style={{ marginTop: 16, background: "#e11d48", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className={`products-grid${sidebarOpen ? " sidebar-open" : ""}`}>
                {paginated.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 36, flexWrap: "wrap" }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p}
                    onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: page === p ? "#e11d48" : "rgba(0,0,0,0.06)", color: page === p ? "#fff" : "#555", fontWeight: page === p ? 700 : 400, fontSize: 13, cursor: "pointer", transition: "all 0.2s" }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sort dropdown backdrop */}
      {sortOpen && (
        <div onClick={() => setSortOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
      )}

      <style>{`
        .cat-page {
          padding: 28px 0 48px;
        }
        .cat-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 12px;
          padding: 10px 16px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          gap: 8px;
        }
        .sort-label {
          color: #999;
          font-size: 13px;
          white-space: nowrap;
        }
        .sort-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          border: 1px solid rgba(0,0,0,0.12);
          border-radius: 8px;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 500;
          color: #1a1a1a;
          cursor: pointer;
          min-width: 160px;
          white-space: nowrap;
        }
        .cat-sidebar-wrap {
          width: 240px;
          flex-shrink: 0;
        }
        .sidebar-backdrop {
          display: none;
        }
        .sidebar-close-btn {
          display: none;
        }
        .products-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 4px;
          width: 100%;
          box-sizing: border-box;
          min-width: 0;
        }
        .products-grid.sidebar-open {
          grid-template-columns: repeat(3, 1fr);
        }

        @media (max-width: 900px) {
          .products-grid { grid-template-columns: repeat(3, 1fr); }
          .products-grid.sidebar-open { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .cat-page { padding: 20px 16px 40px; }
          .cat-sidebar-wrap {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            width: 100%;
            max-height: 80vh;
            overflow-y: auto;
            z-index: 1000;
            border-top-left-radius: 20px;
            border-top-right-radius: 20px;
            box-shadow: 0 -8px 40px rgba(0,0,0,0.18);
          }
          .sidebar-backdrop {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.4);
            z-index: 999;
          }
          .sidebar-close-btn {
            display: flex !important;
          }
        }

        @media (max-width: 580px) {
          .products-grid,
          .products-grid.sidebar-open {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          .sort-label { display: none; }
          .sort-btn { min-width: 0; padding: 7px 10px; font-size: 12px; }
          .cat-toolbar { padding: 8px 12px; }
        }
      `}</style>
    </>
  );
}
